import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import {
    createContext,
    useContext,
    useEffect,
    useId,
    useState,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'

interface AccordionContextValue {
    isOpen: boolean
    toggle: () => void
    headerId: string
    contentId: string
}

const AccordionContext = createContext<AccordionContextValue>({
    isOpen: false,
    toggle: () => {},
    headerId: '',
    contentId: '',
})

export interface AccordionContainerProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title' | 'content' | 'onToggle'
> {
    isOpen?: boolean
    onToggle?: (isOpen: boolean) => void
    children?: ReactNode
}

export function AccordionContainer({
    isOpen = false,
    onToggle,
    children,
    className = '',
    ...restProps
}: AccordionContainerProps) {
    const generatedId = useId()
    const baseId = restProps.id ?? generatedId
    const headerId = `accordion-header-${baseId}`
    const contentId = `accordion-content-${baseId}`

    const [open, setOpen] = useState<boolean>(isOpen)

    useEffect(() => {
        setOpen(isOpen)
    }, [isOpen])

    const toggle = () => {
        const nextState = !open

        setOpen(nextState)
        onToggle?.(nextState)
    }

    return (
        <AccordionContext.Provider
            value={{
                isOpen: open,
                toggle,
                headerId,
                contentId,
            }}
        >
            <div
                {...restProps}
                className={`rounded-xl bg-white border border-stone-200 shadow-xs ${className}`.trim()}
            >
                {children}
            </div>
        </AccordionContext.Provider>
    )
}

export interface AccordionHeaderProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title'
> {
    iconName?: IconNameEnum | string | null
    subtitle?: ReactNode
    children?: ReactNode
}

export function AccordionHeader({
    iconName,
    subtitle,
    children,
    className = '',
    onClick,
    onKeyDown,
    ...restProps
}: AccordionHeaderProps) {
    const { isOpen, toggle, headerId, contentId } = useContext(AccordionContext)

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        onClick?.(e)
        toggle()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e)

        if (
            e.key === KeyboardKeyEnum.Enter ||
            e.key === KeyboardKeyEnum.Space
        ) {
            e.preventDefault()
            toggle()
        }
    }

    return (
        <div
            id={headerId}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            aria-controls={contentId}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            {...restProps}
            className={`p-4 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary focus-visible:outline-none ${
                isOpen ? 'rounded-t-xl' : 'rounded-xl'
            } ${className}`.trim()}
        >
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    {iconName ? (
                        <div className="p-2">
                            <Icon
                                name={iconName as IconNameEnum}
                                className="w-5 h-5 text-gray-500"
                                aria-hidden="true"
                            />
                        </div>
                    ) : null}
                    <div>
                        <div className="text-gray-900 font-medium">
                            {children}
                        </div>
                        {subtitle ? (
                            <div className="text-xs text-gray-700">
                                {subtitle}
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="p-2">
                    <Icon
                        name={
                            isOpen
                                ? IconNameEnum.AngleUp
                                : IconNameEnum.AngleDown
                        }
                        className="w-4 h-4 text-gray-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>
    )
}

export interface AccordionContentProps extends ComponentPropsWithRef<'div'> {
    children?: ReactNode
}

export function AccordionContent({
    children,
    className = '',
    ...restProps
}: AccordionContentProps) {
    const { isOpen, headerId, contentId } = useContext(AccordionContext)

    if (!isOpen) {
        return null
    }

    return (
        <div
            id={contentId}
            role="region"
            aria-labelledby={headerId}
            {...restProps}
            className={`px-6 py-4 ${className}`.trim()}
        >
            <div>{children}</div>
        </div>
    )
}

export function AccordionActions({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...restProps}
            className={`mt-6 flex justify-end gap-2 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export const Accordion = Object.assign(AccordionContainer, {
    Container: AccordionContainer,
    Header: AccordionHeader,
    Content: AccordionContent,
    Actions: AccordionActions,
})
